import { type App, reactive, readonly } from 'vue';
import { registerPartial } from '@precon-tools-suite-by-shared/vue-partials';

// Import the global options.
import { preconAuthenticationOptionsInjectionKey } from './symbols';

import {
    type PreconAuthenticationGlobalOptions,
    normalizePreconAuthenticationOptions,
} from './options';

// Export the
export type { PreconAuthenticationGlobalOptions };

// Export the helpers that you want to use outside of this library.
export * from './helpers/normalizeScopes';
export * from './helpers/isOwnedBy';

// Export the composables that you want to use outside of this library.
export { useAbility, useAbilityWithParams } from './composables/useAbility';
export { useAuthentication } from './composables/useAuthentication';
export { useAuthorization } from './composables/useAuthorization';

// Re-export some useful composables from the @precon-tools-suite-by-shared/vue-msal package.
export {
    useIsAuthenticated,
    useLogin,
    useLogout,
    useAquireToken,
    useMsal,
    InteractionType,
    InteractionStatus,
} from '@precon-tools-suite-by-shared/vue-msal';

// Import/Export the components that you want to use outside of this library.
import PreconTopbarUserProfile from './components/PreconTopbarUserProfile.vue';
import LoginButton from './components/LoginButton.vue';
import LogoutButton from './components/LogoutButton.vue';
import LoginGuard from './components/LoginGuard.vue';
import AbilityGate from './components/AbilityGate.vue';
import AbilityButton from './components/AbilityButton.vue';
export { LoginButton, LogoutButton, LoginGuard, AbilityGate, AbilityButton };

import preconUserProfilesPlugin from '@precon-tools-suite-by-shared/shared-auth-ui';
import { useAuthorizationStore } from './stores/authorization';
import { useMsalStore } from '@precon-tools-suite-by-shared/vue-msal';
import { setupErrorPages } from './router/error-pages';
import { setupRouterGuards } from './router/guards';
import type { Router, RouteLocationRaw } from 'vue-router';

// The definition of the plugin that installs the
export default {
    install(
        app: App,
        options: PreconAuthenticationGlobalOptions & {
            fetchRolesFromBackendIfMissingInToken?: boolean;
            noErrorPages?: boolean;
            router?: Router;
            unauthenticatedRoute?: RouteLocationRaw;
            authenticatedRoute?: RouteLocationRaw;
        }
    ) {
        // Install preconUserProfilesPlugin and msalPlugin, as it MUST be installed anyway and these plugins
        // won't likely be used in other features for now.
        app.use(preconUserProfilesPlugin, options);

        // Initialize the authorization store.
        const authorizationStore = useAuthorizationStore();
        authorizationStore.fetchIfRolesClaimMissing =
            options.fetchRolesFromBackendIfMissingInToken ?? false;

        // Initialize the error routes.
        const router = options.router;

        // Setup error pages and router guards.
        if (router && !options.noErrorPages) {
            setupErrorPages(router);
            setupRouterGuards(router, {
                unauthenticatedRoute: options.unauthenticatedRoute,
                authorizedRoute: options.authenticatedRoute,
            });
        }

        /// NORMALIZE THE OPTIONS
        const normalizedOptions = reactive(
            normalizePreconAuthenticationOptions(options)
        );

        /// INJECT THE OPTIONS
        // This ensures that the options are available to the components in this library.
        app.provide(
            preconAuthenticationOptionsInjectionKey,
            readonly(normalizedOptions)
        );

        // SET THE TOPBAR COMPONENT IN THE LAYOUT
        app.config.globalProperties.$layout.userProfileComponent =
            PreconTopbarUserProfile;

        /// GLOBAL COMPONENT BINDINGS
        // Use this to register the components that you use almost everywhere in the application.
        // WARNING! Do not register large components like this, as it introduces a significant overhead at the application startup.
        app.component('LoginButton', LoginButton);
        app.component('LogoutButton', LogoutButton);
        app.component('LoginGuard', LoginGuard);
        app.component('AbilityGate', AbilityGate);
        app.component('AbilityButton', AbilityButton);

        // PARTIALS
        registerPartial(
            app,
            '@precon-tools-suite-by-shared/shared-auth-feature/partials/MyTeamOverview',
            () => import('./partials/MyTeamOverview.vue')
        );

        registerPartial(
            app,
            '@precon-tools-suite-by-shared/shared-auth-feature/partials/MyPermissions',
            () => import('./partials/MyPermissions.vue')
        );

        const msalStore = useMsalStore();
        msalStore.handleRedirectPromise();
    },
};
